import { useState, useEffect } from "react";
import { Trash2, Pencil, Plus, ShieldAlert, ShieldCheck, ClipboardList, Truck, User } from "lucide-react";
import { Button, Modal, Table, Select, PhoneInput } from "../../../components";
import axios from "axios";
import { GET_USERS_URL, DELETES_USER_URL } from "../../../features/UrlHelper";

const ROLE_ICONS = {
  SUPERADMIN: ShieldAlert,
  ADMIN:      ShieldCheck,
  MANAGER:    ClipboardList,
  DRIVER:     Truck,
};

function UsersSection() {

  // SUPERADMIN je platformova rola bez firmy -- v sprave prevadzky sa neprideluje
  // nikomu (backend vracia 403). Spravcovia systemu sa spravuju v /sprava.
  const availableRoles = ['DRIVER', 'MANAGER', 'ADMIN'];
  const defaultRole = availableRoles[0] || 'DRIVER';

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [roleFilter, setRoleFilter] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Add / Edit modal
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userPhoneDialCode, setUserPhoneDialCode] = useState("+421");
  const [userPhoneLocal, setUserPhoneLocal] = useState("");
  const [userForm, setUserForm] = useState({
    id: null,
    username: "",
    firstname: "",
    lastname: "",
    email: "",
    phone: "",
    role: defaultRole,
    is_verified: false,
  });
  const [isSubmittingUser, setIsSubmittingUser] = useState(false);

  // Temp password modal
  const [tempPasswordModal, setTempPasswordModal] = useState({ open: false, password: "" });

  useEffect(() => {
    loadUsers();
  }, []);

  async function loadUsers() {
    try {
      const response = await axios.get(GET_USERS_URL);
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoadingUsers(false);
    }
  }

  const openAddUser = () => {
    setEditingUser(null);
    setUserPhoneDialCode("+421");
    setUserPhoneLocal("");
    setUserForm({
      id: null,
      username: "",
      firstname: "",
      lastname: "",
      email: "",
      phone: "",
      role: defaultRole,
      is_verified: false,
    });
    setUserModalOpen(true);
  };

  const openEditUser = (user) => {
    console.log("Editing user:", user);
    const rawPhone = user.phone || "";
    const PHONE_PREFIXES = ["+421", "+420"];
    const dialCode = PHONE_PREFIXES.find((p) => rawPhone.startsWith(p)) ?? "+421";
    const localNum = rawPhone.startsWith(dialCode) ? rawPhone.slice(dialCode.length) : rawPhone;
    setUserPhoneDialCode(dialCode);
    setUserPhoneLocal(localNum);
    setEditingUser(user);
    setUserForm({
      id: user.id,
      username: user.username || "",
      firstname: user.firstname || "",
      lastname: user.lastname || "",
      email: user.email || "",
      phone: rawPhone,
      role: (user.role || defaultRole).toUpperCase(),
      is_verified: user.is_verified || false,
    });
    setUserModalOpen(true);
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingUser(true);
    const data = {
      id: userForm.id,
      username: userForm.username,
      firstname: userForm.firstname,
      lastname: userForm.lastname,
      email: userForm.email,
      phone: userForm.phone,
      role: userForm.role,
      is_verified: userForm.is_verified,
    };

    try {
      if (editingUser) {
        await axios.post(GET_USERS_URL, data);
        await loadUsers();
        setUserModalOpen(false);
      } else {
        const res = await axios.put(GET_USERS_URL, data);
        await loadUsers();
        setUserModalOpen(false);
        if (res.data.tempPassword) {
          setTempPasswordModal({ open: true, password: res.data.tempPassword });
        }
      }
    } catch (error) {
      console.error("Failed to save user:", error);
      alert(error?.response?.data?.message || "Nastala chyba pri ukladaní používateľa.");
    } finally {
      setIsSubmittingUser(false);
    }
  };

  const deleteUser = async (id) => {
    setIsDeleting(true);
    try {
      await axios.delete(DELETES_USER_URL(id));
      setUsers((prev) => prev.filter((u) => u.id !== id));
      setDeleteConfirm(null);
    } catch (error) {
      console.error("Chyba pri mazaní používateľa:", error);
      alert(error?.response?.data?.message || "Nastala chyba pri mazaní používateľa.");
    } finally {
      setIsDeleting(false);
    }
  };

  const roleLabel = (role) => {
    switch ((role || "").toUpperCase()) {
      case "SUPERADMIN": return "Superadmin";
      case "ADMIN":      return "Admin";
      case "MANAGER":    return "Manažér";
      case "DRIVER":     return "Vodič";
      case "CUSTOMER":   return "Zákazník";
      default:           return role || "-";
    }
  };

  const roleOptions = availableRoles.map((r) => {
    const Icon = ROLE_ICONS[r] ?? User;
    return {
      value: r,
      label: (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Icon size={15} />
          {roleLabel(r)}
        </span>
      ),
    };
  });

  return (
    <div className="admin__section">
      <div className="admin__sub-header">
        <h2 className="admin__section-title">Správa používateľov</h2>
        <Button variant="primary" onClick={openAddUser}>
          <Plus size={16} /> Pridať používateľa
        </Button>
      </div>

      <div className="admin__table-wrapper">
        <Table
          columns={[
          { key: "username", label: "Login" },
            { key: "firstname", label: "Meno" },
            { key: "lastname", label: "Priezvisko" },
            { key: "email", label: "Email" },
            { key: "phone", label: "Telefón" },
            {
              key: "role",
              label: "Rola",
              render: (val) => <>{roleLabel(val)}</>,
            },
            {
              key: "is_verified",
              label: "Overený",
              render: (val) => (val ? "Áno" : "Nie"),
            },
            {
              key: "actions",
              label: "Akcie",
              sortable: false,
              render: (_, row) => (
                <div className="admin__table-actions">
                  <button
                    className="admin__icon-btn"
                    onClick={() => openEditUser(row)}
                    title="Upraviť"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    className="admin__icon-btn admin__icon-btn--danger"
                    onClick={() => setDeleteConfirm(row.id)}
                    title="Zmazať"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ),
            },
          ]}
          data={roleFilter ? users.filter((u) => u.role === roleFilter) : users}
          searchPlaceholder="Hľadať užívateľa..."
          emptyMessage="Žiadni užívatelia"
          toolbarContent={
            <Select
              value={roleFilter}
              onChange={(val) => setRoleFilter(val)}
              options={[
                { value: "", label: "Všetky roly" },
                ...roleOptions,
              ]}
            />
          }
        />
      </div>

      {/* Add / Edit user modal */}
      <Modal
        isOpen={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        title={editingUser ? "Upraviť užívateľa" : "Pridať užívateľa"}
      >
        <form className="admin__form" onSubmit={handleUserSubmit}>
          <div className="admin__form-field">
            <label className="admin__form-label">Prihlasovacie meno</label>
            <input
              maxLength={50}
              className="admin__form-input"
              value={userForm.username}
              onChange={(e) =>
                setUserForm((prev) => ({ ...prev, username: e.target.value }))
              }
              placeholder="jan.novak"
              required
              autoCapitalize="none"
              spellCheck={false}
            />
          </div>
          <div className="admin__form-row">
            <div className="admin__form-field">
              <label className="admin__form-label">Meno</label>
              <input
                maxLength={255}
                className="admin__form-input"
                value={userForm.firstname}
                onChange={(e) =>
                  setUserForm((prev) => ({
                    ...prev,
                    firstname: e.target.value,
                  }))
                }
                placeholder="Meno"
                required
              />
            </div>
            <div className="admin__form-field">
              <label className="admin__form-label">Priezvisko</label>
              <input
                maxLength={255}
                className="admin__form-input"
                value={userForm.lastname}
                onChange={(e) =>
                  setUserForm((prev) => ({
                    ...prev,
                    lastname: e.target.value,
                  }))
                }
                placeholder="Priezvisko"
                required
              />
            </div>
          </div>
          <div className="admin__form-field">
            <label className="admin__form-label">Email</label>
            <input
              maxLength={255}
              type="email"
              className="admin__form-input"
              value={userForm.email}
              onChange={(e) =>
                setUserForm((prev) => ({ ...prev, email: e.target.value }))
              }
              placeholder="email@example.com"
              disabled={!!editingUser}
              autoComplete="new-user-email"
            />
          </div>
          <div className="admin__form-field">
            <label className="admin__form-label">Telefónne číslo</label>
            <PhoneInput
              value={userPhoneLocal}
              prefix={userPhoneDialCode}
              onChange={(local) => {
                setUserPhoneLocal(local);
                setUserForm((prev) => ({ ...prev, phone: userPhoneDialCode + local }));
              }}
              onPrefixChange={(dial) => {
                setUserPhoneDialCode(dial);
                setUserForm((prev) => ({ ...prev, phone: dial + userPhoneLocal }));
              }}
            />
          </div>
          <div className="admin__form-field">
            <Select
              label="Rola"
              options={roleOptions}
              value={userForm.role}
              onChange={(val) => setUserForm((prev) => ({ ...prev, role: val }))}
            />
          </div>
          <div className="admin__form-field-checkbox">
            <label className="admin__form-label">Overený</label>
            <input
              type="checkbox"
              className="admin__form-checkbox"
              checked={userForm.is_verified || false}
              onChange={(e) =>
                setUserForm((prev) => ({
                  ...prev,
                  is_verified: e.target.checked,
                }))
              }
            />
          </div>

          <div className="admin__form-actions">
            <Button
              variant="ghost"
              type="button"
              onClick={() => setUserModalOpen(false)}
              disabled={isSubmittingUser}
            >
              Zrušiť
            </Button>
            <Button variant="primary" type="submit" disabled={isSubmittingUser}>
              {isSubmittingUser ? "Ukladám..." : editingUser ? "Uložiť zmeny" : "Pridať užívateľa"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Temp password modal */}
      <Modal
        isOpen={tempPasswordModal.open}
        onClose={() => setTempPasswordModal({ open: false, password: "" })}
        title="Používateľ vytvorený"
      >
        <div className="admin__temp-password-modal">
          <p>Používateľ bol úspešne vytvorený. Dočasné heslo pre prvé prihlásenie:</p>
          <div className="admin__temp-password-box">
            <strong>{tempPasswordModal.password}</strong>
          </div>
          <p className="admin__temp-password-warning">
            Skopírujte si toto heslo a odovzdajte ho používateľovi. Heslo sa už nezobrazí.
            Používateľ si ho bude musieť zmeniť pri prvom prihlásení.
          </p>
          <div className="admin__form-actions">
            <Button
              variant="primary"
              onClick={() => {
                navigator.clipboard?.writeText(tempPasswordModal.password);
                setTempPasswordModal({ open: false, password: "" });
              }}
            >
              Skopírovať a zatvoriť
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete confirmation */}
      <Modal
        isOpen={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        title="Potvrdiť zmazanie"
      >
        <p>Naozaj chcete zmazať tohto užívateľa? Táto akcia sa nedá vrátiť.</p>
        <div className="admin__form-actions">
          <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>
            Zrušiť
          </Button>
          <Button
            variant="primary"
            onClick={() => deleteUser(deleteConfirm)}
            disabled={isDeleting}
            style={{ backgroundColor: "var(--c-error)", borderColor: "var(--c-error)" }}
          >
            {isDeleting ? "Mazanie..." : "Zmazať"}
          </Button>
        </div>
      </Modal>
    </div>
  );
}

export default UsersSection;
